import { AlertTriangle, Check, X } from "lucide-react";

import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtDateTime } from "@/lib/date";

import { useKbRuns, type RunRow } from "../api";

function YesNo({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  return value ? (
    <Check className="h-4 w-4 text-success" aria-label="yes" />
  ) : (
    <X className="h-4 w-4 text-danger" aria-label="no" />
  );
}

function num(v: number | null): string {
  return v === null ? "—" : v.toLocaleString();
}

function Row({ run }: { run: RunRow }) {
  const alarming = run.result === "auth_failed" || run.result === "failed";
  return (
    <TableRow className={alarming ? "bg-danger/5" : undefined}>
      <TableCell className="whitespace-nowrap tabular-nums">
        {fmtDateTime(run.finished_at)}
      </TableCell>
      <TableCell>
        <StatusBadge value={run.result} />
      </TableCell>
      <TableCell className="text-center">
        <YesNo value={run.auth_ok} />
      </TableCell>
      <TableCell className="text-center">
        <YesNo value={run.crawl_complete} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.articles_discovered)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.articles_changed)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.articles_deleted)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.docs_indexed)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.chunks_upserted)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(run.points_total)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {run.duration_s === null ? "—" : `${Math.round(run.duration_s / 60)}m`}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {run.error_count ? (
          <span className="text-danger">{run.error_count}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function RunsTab() {
  const { data, isPending, error } = useKbRuns();

  if (isPending) return <Skeleton className="h-64" />;

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load run history</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data.available) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Run history unavailable</AlertTitle>
        <AlertDescription>
          The pipeline&rsquo;s status API could not be reached
          {data.reason ? ` (${data.reason})` : ""}. It runs beside the index on
          the Spark host; if the index is reachable and this is not, the
          kb-status container is down.
        </AlertDescription>
      </Alert>
    );
  }

  const runs = data.runs;
  const recent = runs.slice(0, 30);
  const good = recent.filter(
    (r) => r.result === "ok" || r.result === "ok_no_changes",
  ).length;
  const loginFailures = recent.filter((r) => r.result === "auth_failed").length;
  const changed = recent.reduce((s, r) => s + (r.articles_changed ?? 0), 0);
  const deleted = recent.reduce((s, r) => s + (r.articles_deleted ?? 0), 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Good nights"
          value={`${good} / ${recent.length}`}
          sub="last 30 runs, OK or OK-no-changes"
          tone={
            recent.length && good === recent.length
              ? "positive"
              : good < recent.length - 2
                ? "warning"
                : "neutral"
          }
        />
        <KpiTile
          label="Login failures"
          value={loginFailures}
          sub="runs that could not sign in"
          tone={loginFailures ? "danger" : "positive"}
        />
        <KpiTile
          label="Articles changed"
          value={changed.toLocaleString()}
          sub="across the last 30 runs"
        />
        <KpiTile
          label="Articles deleted"
          value={deleted.toLocaleString()}
          sub="removed from the index"
        />
      </div>

      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Finished</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="text-center">Signed in</TableHead>
                <TableHead className="text-center">Complete</TableHead>
                <TableHead className="text-right">Found</TableHead>
                <TableHead className="text-right">Changed</TableHead>
                <TableHead className="text-right">Deleted</TableHead>
                <TableHead className="text-right">Indexed</TableHead>
                <TableHead className="text-right">Sections</TableHead>
                <TableHead className="text-right">Points</TableHead>
                <TableHead className="text-right">Took</TableHead>
                <TableHead className="text-right">Errors</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 ? (
                <TableEmpty colSpan={12}>
                  No runs recorded yet — the first ingest has not happened.
                </TableEmpty>
              ) : (
                runs.map((r, i) => <Row key={r.run_id ?? i} run={r} />)
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
